import React from 'react';
import { createMemoFn } from '@/lib/propHelpers';
import { createOrderMap, move } from '@/lib/array';
import { FormattedMessage } from 'react-intl';

import ColumnOption from '@/components/Tables/layout/JELinesTable/VisibilityColsDropdown/ColumnOption';
import ColSortableList from '@/components/Tables/layout/JELinesTable/VisibilityColsDropdown/colSortableList';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import ListItem from '@mui/material/ListItem';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { styled } from '@mui/material/styles';

const JELineColumnsManageDropdown = styled('div', { label: 'JELineColumnsManageDropdown' })(() => ({
  position: 'absolute',
  top: 0,
  right: -28,
  zIndex: 10,
}));

class ColsDropdown extends React.Component {
  constructor() {
    super();
    this.state = {
      cacheOrder: null,
    };
  }

  componentDidUpdate(prevProps) {
    const { order } = this.props;
    const { cacheOrder } = this.state;

    if (prevProps.order !== order && cacheOrder) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ cacheOrder: null });
    }
  }

  onChange = (colName) => () => {
    const { onChange, disabledCols } = this.props;
    const newDisabledCols = disabledCols.includes(colName)
      ? disabledCols.filter((c) => c !== colName)
      : [...disabledCols, colName];
    onChange(newDisabledCols);
  };

  getNewOrder = ({ oldIndex: from, newIndex: to }, order, list) => {
    if (from !== to) {
      const listOrder = list.map((i) => i.colName);

      const item = listOrder[from];

      // order columns in order map
      const newOrder = move(listOrder, from, to);

      // define already ordering columns
      const existsCols = order.filter((c) => c !== '*');

      // add new ordered column
      const nextExistsCols = existsCols.includes(item) ? existsCols : [...existsCols, item];

      // extract new ordering map from new columns order
      return createOrderMap(newOrder, nextExistsCols);
    }
    return order;
  };

  onSortEndPinned = (arg) => {
    const { order, onChangeOrder } = this.props;
    const newOrder = this.getNewOrder(arg, order.pinned, this.getPinnedCols());
    onChangeOrder(order.set('pinned', newOrder));
  };

  onSortFloatPinned = (arg) => {
    const { order, onChangeOrder } = this.props;
    const newOrder = this.getNewOrder(arg, order.float, this.getFloatCols());
    onChangeOrder(order.set('float', newOrder));
  };

  getPinnedCols = createMemoFn(
    () => {
      const { namedCols, pinnedCols } = this.props;
      return { namedCols, pinnedCols };
    },
    ({ namedCols, pinnedCols }) => {
      const cols = namedCols.filter((nCol) => pinnedCols.includes(nCol.colName));
      return cols;
    },
  );

  getFloatCols = createMemoFn(
    () => {
      const { namedCols, pinnedCols } = this.props;
      return { namedCols, pinnedCols };
    },
    ({ namedCols, pinnedCols }) => namedCols.filter((nCol) => !pinnedCols.includes(nCol.colName)),
  );

  isSelected = (colName) => {
    const { disabledCols, pinnedCols } = this.props;
    return !disabledCols.includes(colName) || pinnedCols.includes(colName);
  };

  list = (filteredNamedCols, isPinned) => {
    const { pinnedCols, forcedEnabledCols } = this.props;
    const countSelected = filteredNamedCols.reduce((res, nCol) => (this.isSelected(nCol.colName) ? res + 1 : res), 0);
    const onSortEnd = isPinned ? this.onSortEndPinned : this.onSortFloatPinned;
    return (
      <ColSortableList items={filteredNamedCols} helperClass="is-dragging" onSortEnd={onSortEnd} useDragHandle>
        {(nCol) => {
          const checked = this.isSelected(nCol.colName);
          const isDisabled =
            (checked && !isPinned && countSelected === 1) ||
            forcedEnabledCols.includes(nCol.colName) ||
            pinnedCols.includes(nCol.colName);

          return (
            <ColumnOption
              column={nCol}
              isPinned={isPinned}
              checked={checked}
              isDisabled={isDisabled}
              onChange={this.onChange(nCol.colName)}
            />
          );
        }}
      </ColSortableList>
    );
  };

  render() {
    const { onResetColumnsWidth } = this.props;

    const pinedNamedCols = this.getPinnedCols();
    const floatNamedCols = this.getFloatCols();

    return (
      <PopupState variant="popover" popupId="jeColumnsManageMenu">
        {(popupState) => (
          <JELineColumnsManageDropdown>
            <IconButton size="small" color="primary" {...bindTrigger(popupState)}>
              {popupState.isOpen ? <CancelIcon fontSize="18" /> : <AddCircleIcon fontSize="18" />}
            </IconButton>
            <Menu
              {...bindMenu(popupState)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
              slotProps={{ paper: { sx: { maxHeight: 450 } }, list: { component: 'div' } }}
            >
              {onResetColumnsWidth && (
                <ListItem component="div">
                  <Button variant="outlined" fullWidth size="small" onClick={onResetColumnsWidth}>
                    <FormattedMessage id="document.show.journalEntry.autoSize" defaultMessage="Autosize columns" />
                  </Button>
                  <Divider />
                </ListItem>
              )}
              {!!pinedNamedCols.length && this.list(pinedNamedCols, true)}
              {!!pinedNamedCols.length && !!floatNamedCols.length && <Divider component="div" />}
              {!!floatNamedCols.length && this.list(floatNamedCols, false)}
            </Menu>
          </JELineColumnsManageDropdown>
        )}
      </PopupState>
    );
  }
}

ColsDropdown.displayName = 'ColsDropdown';

export default ColsDropdown;
